import React from "react";
import {startCase} from "lodash";
import {TaskAttachments, TaskSection} from "view/components/TaskDrawer";
import RichText from "view/components/RichText";
import type {
  AssignedTaskFormValue,
  MultitaskFormValue,
  TaskFormValues as TaskResponse,
} from "view/components/TasksList/types";
import {GridTaskLike} from "ts/api/grid-task-like";
import {AttachmentFile} from "ts/api/attachmentFile";
import {Box, IconButton} from "@material-ui/core";
import HistoryIcon from "@material-ui/icons/History";
import EditIcon from "@material-ui/icons/Edit";
import {grey} from "@material-ui/core/colors";
import {unpackFileAttachment} from "tools/product/mapAttachmentFieldToTaskAttachment";
import {GridProps} from "view/components/GridResponseBlock/CustomizeTable/Components/Grid";
import {IShow, TaskResponseType} from "@evennt-tech/api-v2-core";
import {IGridSchema} from "ts/api/grid-schema";
import {
  drawerGridToFormAnswer,
  overlayRequiredFromDefinition,
  snapshotGridToForm,
} from "tools/task/grid/mappers";
import {
  IDrawerTaskInstanceGridPayload,
  IDrawerTaskInstanceResponsePayload,
  IGetOneByIdTaskOption,
} from "@evennt-tech/api-v2-external";
import {NOT_APPLICABLE_FULL_DISPLAY} from "tools/task/constants";
import {useTranslate} from "@tolgee/react";
import CONFIRMED_LABEL from "../TaskDetailsDrawer/confirmedLabel";
import {buildGridPreviewTask} from "./helpers/buildGridPreviewTask";
import CutGrid from "./components/CutGrid";
import CheckboxesResponse from "./components/CheckboxesResponse";
import MultipleChoiceResponse from "./components/MultipleChoiceResponse";
import GridResponse from "./components/Grid";
import GridPreview from "./components/Grid/Preview";

/**
 * Renders the current response for a task instance. The response value
 * comes from the wire-native `apiV2` payload; the `task` prop supplies
 * non-response data (question definition, options, grid schema).
 */
type ResponseBlockProps = Pick<GridProps, "headCellSize"> & {
  task?: AssignedTaskFormValue;
  onClickHistoryButton?: (subtaskId?: string) => void;
  /**
   * Render an Edit (pencil) icon next to the History icon; click opens
   * the response modal for editing. Used inside a multitask subtask row;
   * left undefined for the task-level drawer.
   */
  onClickEdit?: () => void;
  hideHeader?: boolean;
  /**
   * Used only by the `GridPreview` branch (`grid?.showPreview`). Optional
   * because other paths never trigger that branch, but callers in
   * showPreview mode MUST pass a full `IShow`.
   */
  show?: IShow;
  isSubtask?: boolean;
  onSaveDraft?: (
    formData: Partial<Omit<TaskResponse, "responseType">>,
    subtask?: MultitaskFormValue
  ) => void;
  grid?: {
    showPreview?: boolean;
    cutVersion?: boolean;
  };
  /**
   * apiV2 drawer-state input. When set, drives both the current-response
   * display and the History icon visibility (icon renders when
   * `hasHistory` is true; click hands off to `onClickHistoryButton`).
   */
  apiV2?: {
    responseType: TaskResponseType;
    payload: IDrawerTaskInstanceResponsePayload | null;
    hasHistory: boolean;
    /**
     * task_responses.id of the shown version — threaded to the grid "Open Grid"
     * button so the standalone preview loads it from the endpoint.
     */
    responseId?: string;
    /** Grid schema, required for rendering a GRID response. */
    gridSchema?: IGridSchema | null;
    /**
     * Option rows, required for checkboxes / multipleChoice to resolve
     * human-readable labels — the payload only carries optionId(s).
     */
    options?: IGetOneByIdTaskOption[];
    /**
     * Exhibitor used the "Not Applicable" opt-out. NA is an instance
     * flag with no response payload, so this block synthesizes the
     * display: SHORT_ANSWER_ROWS renders "Row 1: Not Applicable",
     * every other type renders the sentinel text.
     */
    isNotApplicable?: boolean;
    /**
     * FILES rows of a past version that the latest one dropped: shown
     * without download or copy-link actions (FS-1572).
     */
    goneFileIds?: ReadonlySet<string>;
  };
};

/**
 * Extract the per-type response slice from the payload and shape it to
 * match what the subcomponents expect. Returns `undefined` when the
 * payload doesn't carry the requested type (e.g. unsubmitted task).
 */
const extractResponseFromPayload = (
  responseType: TaskResponseType,
  payload: IDrawerTaskInstanceResponsePayload | null
): unknown => {
  if (!payload) return undefined;
  switch (responseType) {
    case TaskResponseType.SHORT_ANSWER:
      return payload.shortAnswer?.text;
    case TaskResponseType.PARAGRAPH:
      return payload.paragraph?.text;
    case TaskResponseType.CHECK:
      // A CHECK response is a confirmation tap — render the "Confirmed"
      // label, not the raw boolean the payload carries.
      return payload.check?.isChecked ? CONFIRMED_LABEL : undefined;
    case TaskResponseType.SHORT_ANSWER_ROWS:
      // Rows are unordered; sort by `rowIndex`.
      return payload.shortAnswerRows?.rows
        .slice()
        .sort((a, b) => a.rowIndex - b.rowIndex)
        .map((r) => r.text);
    case TaskResponseType.FILES:
      return payload.files?.files;
    case TaskResponseType.MULTIPLE_CHOICE:
      return payload.multipleChoice?.optionId;
    case TaskResponseType.CHECKBOXES:
      return payload.checkboxes?.optionIds;
    case TaskResponseType.GRID:
      return payload.grid;
    default:
      return undefined;
  }
};

// NOTE: Don't use frontend hooks inside this file, it's used for public review by email page
const ResponseBlock: React.FC<ResponseBlockProps> = ({
  task,
  hideHeader,
  isSubtask,
  show,
  onSaveDraft,
  onClickHistoryButton,
  onClickEdit,
  grid,
  headCellSize,
  apiV2,
}) => {
  const {t} = useTranslate();
  // Question definition (options, grid schema) always comes off `task`;
  // apiV2 mode only overrides the response read path.
  const responseType = apiV2?.responseType ?? task?.responseType;
  if (!responseType) {
    return null;
  }

  // Read the current response from the payload. For an NA opt-out (no
  // payload) synthesize the sentinel answer so the drawer still shows a
  // response.
  const response = apiV2
    ? apiV2.isNotApplicable
      ? responseType === TaskResponseType.SHORT_ANSWER_ROWS
        ? [NOT_APPLICABLE_FULL_DISPLAY]
        : NOT_APPLICABLE_FULL_DISPLAY
      : extractResponseFromPayload(apiV2.responseType, apiV2.payload)
    : undefined;

  // Resolved due-date for the grid sub-tree. Wire rows carry the computed
  // value on `resolvedDueDate` (raw `dueDate` is null for offset-based
  // due dates); other tasks carry it on `dueDate`.
  const gridDueDateRaw =
    (task as {resolvedDueDate?: Date | string | null} | undefined)
      ?.resolvedDueDate ??
    task?.dueDate ??
    null;
  const gridDueDate: Date | null = gridDueDateRaw
    ? new Date(gridDueDateRaw)
    : null;

  if (grid?.showPreview) {
    // GridPreview reads the schema off `task.gridSchema` and the draft answer
    // off `task.grid` (the positional form-grid shape). In apiV2 mode the slim
    // `task` carries neither, and `response` is the RAW drawer payload
    // (`{rows, cells}`) — NOT the form grid — so reconstruct both (same as the
    // inline render path) before grafting them on, or the preview shows empty.
    if (!task || !show) return null;
    let previewTask: AssignedTaskFormValue;
    if (apiV2) {
      const payloadGrid = response as
        | IDrawerTaskInstanceGridPayload
        | undefined;
      // Preview the CURRENT draft against the LIVE definition: it carries the
      // full OPTIONS option lists, so the read-only Select shows the picked
      // value (a snapshot seeds only the chosen value, which a single-item
      // Select can render oddly). Answer cells are positioned by the snapshot
      // fallback inside `drawerGridToFormAnswer`, which also returns added-row
      // names (the renderer reads added-row titles off `additionalRowNames`).
      // Fall back to the snapshot only when there's no live schema — there the
      // added-row names ride along on `gridSchema.rows` instead.
      const reconstructed = apiV2.gridSchema
        ? drawerGridToFormAnswer(apiV2.gridSchema, payloadGrid)
        : {...snapshotGridToForm(payloadGrid), additionalRowNames: undefined};
      previewTask = buildGridPreviewTask({
        task,
        gridDueDate,
        apiV2: {gridSchema: reconstructed.gridSchema},
        response: reconstructed.grid as AssignedTaskFormValue["grid"],
        additionalRowNames: reconstructed.additionalRowNames,
      });
    } else {
      previewTask = buildGridPreviewTask({
        task,
        gridDueDate,
        response: response as AssignedTaskFormValue["grid"] | undefined,
      });
    }
    return <GridPreview task={previewTask} onContinueResponse={onClickEdit} />;
  }

  // Bail when the response slot is empty (no submit yet / untouched task).
  if (response === undefined || response === null) {
    return null;
  }

  const onClickAttachmentsHistoryButton = () => {
    if (!onClickHistoryButton) {
      return;
    }

    onClickHistoryButton();
  };

  const renderResponseBlock = () => {
    // NA opt-out: for types whose renderers need real artifacts (option
    // ids, file rows, grid answers) show the sentinel as plain text.
    // SHORT_ANSWER_ROWS and scalar types fall through to their normal
    // branches with the synthesized sentinel string.
    if (
      apiV2?.isNotApplicable &&
      ([
        TaskResponseType.CHECKBOXES,
        TaskResponseType.MULTIPLE_CHOICE,
        TaskResponseType.FILES,
        TaskResponseType.GRID,
      ] as TaskResponseType[]).includes(responseType as TaskResponseType)
    ) {
      return <Box>{NOT_APPLICABLE_FULL_DISPLAY}</Box>;
    }
    switch (responseType) {
      case "files": {
        const files = (response as AttachmentFile[]) ?? [];

        return (
          <TaskAttachments
            readonly
            hideTitle={hideHeader}
            onClikHistoryButton={
              onClickHistoryButton ? onClickAttachmentsHistoryButton : undefined
            }
            onClickEditSubTask={isSubtask ? onClickEdit : undefined}
            title={
              !hideHeader ? t("common.tasks.response.files_title") : undefined
            }
            attachments={files.map(unpackFileAttachment)}
            goneFileIds={apiV2?.goneFileIds}
          />
        );
      }
      case "checkboxes": {
        const checkboxes = (response as string[]) ?? [];
        // Resolve human-readable labels from `apiV2.options`; fall back to
        // raw IDs when options aren't available.
        if (apiV2?.options) {
          return (
            <CheckboxesResponse
              checkboxes={checkboxes}
              options={apiV2.options}
            />
          );
        }
        return (
          <Box display="flex" flexDirection="column">
            {checkboxes.map((id) => (
              <Box key={id} pb={0.5}>
                {id}
              </Box>
            ))}
          </Box>
        );
      }
      case "multipleChoice": {
        const multipleChoice = (response as string) ?? "";
        if (apiV2?.options) {
          return (
            <MultipleChoiceResponse
              multipleChoice={multipleChoice}
              options={apiV2.options}
            />
          );
        }
        return <Box>{multipleChoice}</Box>;
      }
      case "shortAnswerRows": {
        const rows = response as string[] | undefined;

        return (
          <Box display="flex" flexDirection="column">
            {rows?.map((row, index) => (
              <Box key={index} pb={0.5}>
                {t("common.tasks.response.row_number", {number: index + 1})}{" "}
                {row}
              </Box>
            ))}
          </Box>
        );
      }
      case "paragraph":
      case "check": {
        const content = response as string | undefined;
        return <RichText content={content} />;
      }
      case "grid": {
        // gridSchema from `apiV2.gridSchema` when available, else from
        // `task.gridSchema`. Renders a placeholder when neither path
        // produces a renderable schema.
        const taskHasInlineSchema = !!task?.gridSchema;
        const schemaFromApiV2 = apiV2?.gridSchema;
        const isSchemaRenderable =
          !!schemaFromApiV2 &&
          Array.isArray(schemaFromApiV2.columns) &&
          schemaFromApiV2.columns.length > 0 &&
          Array.isArray(schemaFromApiV2.rows) &&
          !!schemaFromApiV2.cells &&
          typeof schemaFromApiV2.cells === "object";
        // DISPLAY renders from the per-cell immutable snapshot (structure +
        // labels captured at submit time) so a later definition edit never
        // changes how this (or any history) version looks. When the version
        // has no answer yet, fall back to the live definition to draw the
        // empty grid.
        const payloadGrid = response as
          | IDrawerTaskInstanceGridPayload
          | undefined;
        const fromSnapshot = snapshotGridToForm(payloadGrid);
        const {
          gridSchema: expandedApiV2Schema,
          grid: gridAnswer,
          additionalRowNames: derivedAddedRowNames,
        } = fromSnapshot.gridSchema
          ? {...fromSnapshot, additionalRowNames: [] as string[]}
          : drawerGridToFormAnswer(schemaFromApiV2 ?? null, payloadGrid);
        // The snapshot carries no `required` (definition property) — graft the
        // mandatory-cell markers back from the live definition, same as the
        // standalone GridPreview page does.
        overlayRequiredFromDefinition(
          expandedApiV2Schema,
          schemaFromApiV2 ?? task?.gridSchema ?? null
        );
        const gridTask: GridTaskLike | undefined = taskHasInlineSchema
          ? task
          : isSchemaRenderable
          ? {
              // The submit-time snapshot (`snapshotGridToForm`) hardcodes the
              // table `name` to "" — the answer payload carries per-cell
              // column/row labels but not the table name (a definition
              // property). Graft the live table name so the standalone
              // preview's "Table: <name>" header isn't blank.
              // `includeRowTitles` is a definition property too (ONLY COLUMNS
              // vs ROWS & COLUMNS); the snapshot can only INFER it from the row
              // labels it captured, so the live value wins when we have it —
              // otherwise an only-columns table renders a row-title column here.
              gridSchema: expandedApiV2Schema
                ? {
                    ...expandedApiV2Schema,
                    name: schemaFromApiV2?.name || expandedApiV2Schema.name,
                    includeRowTitles:
                      schemaFromApiV2?.includeRowTitles ??
                      expandedApiV2Schema.includeRowTitles,
                  }
                : expandedApiV2Schema,
              additionalRowNames: derivedAddedRowNames,
              dueDate: gridDueDate,
              title: task?.title ?? null,
              stand: task?.stand,
              company: task?.company,
            }
          : undefined;
        if (!gridTask) {
          return (
            <Box style={{color: "#888"}}>
              {t("common.tasks.grid.unavailable_open_history")}
            </Box>
          );
        }
        return grid?.cutVersion ? (
          <CutGrid task={gridTask} responseId={apiV2?.responseId} />
        ) : (
          <GridResponse
            response={gridAnswer as TaskResponse["grid"]}
            task={gridTask}
            headCellSize={headCellSize}
            responseId={apiV2?.responseId}
          />
        );
      }
      case TaskResponseType.CHECK:
        return (
          <Box>
            {response === CONFIRMED_LABEL
              ? t("common.tasks.response.confirmed", CONFIRMED_LABEL)
              : (response as React.ReactNode)}
          </Box>
        );
      default:
        return <Box>{response as React.ReactNode}</Box>;
    }
  };

  // FILES normally blanks the section title because `TaskAttachments`
  // renders its own heading. The NA opt-out shows a plain sentinel `Box`
  // instead, so keep the section title in that case.
  const responseSectionTitle =
    responseType === "files" && !apiV2?.isNotApplicable
      ? ""
      : t("common.tasks.response.type_title", {
          type: startCase(responseType),
        });

  if (hideHeader) {
    return (
      <TaskSection>
        <>{renderResponseBlock()}</>
      </TaskSection>
    );
  }
  // Show the History icon when the caller wired `onClickHistoryButton`;
  // in apiV2 mode also require `apiV2.hasHistory` to avoid an empty modal.
  const shouldShowHistoryIcon =
    !!onClickHistoryButton && (apiV2 ? apiV2.hasHistory : true);

  const renderIcons = () => {
    return (
      <Box>
        {shouldShowHistoryIcon && (
          <IconButton
            id="cy-open-history-button"
            onClick={() =>
              onClickHistoryButton?.(isSubtask ? task?.id : undefined)
            }
          >
            <HistoryIcon htmlColor={grey[500]} />
          </IconButton>
        )}
        {/* The edit pencil is the only way to edit a SUBTASK response.
            Standalone tasks edit via the [EDIT TASK] button, so the pencil
            is shown for subtasks only. */}
        {onClickEdit && isSubtask && (
          <IconButton onClick={onClickEdit}>
            <EditIcon />
          </IconButton>
        )}
      </Box>
    );
  };
  return (
    <TaskSection title={responseSectionTitle} actionPanel={renderIcons()}>
      <>{renderResponseBlock()}</>
    </TaskSection>
  );
};

export default ResponseBlock;
